/**
 * 🟥 CUADRO NEGRO V5 — CommitHistoryPanel
 *
 * Modal/panel que lista los commits del repo asociado al módulo.
 * Por commit ofrece:
 *   - Ver en GitHub (link).
 *   - Comparar con Workspace (POST /api/github/compare → DiffViewer).
 *   - Revertir (POST /api/github/revert con confirmación).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { History, ExternalLink, GitCompare, RotateCcw, X, Loader2, GitCommit } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { BACKEND_URL } from '../config/api';
import DiffViewer from './DiffViewer';

const API = `${BACKEND_URL}/api`;

export const CommitHistoryPanel = ({ moduleId, onClose }) => {
  const { language } = useLanguage();
  const isEs = (language || 'es').toLowerCase().startsWith('es');

  const [commits, setCommits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // sha actualmente en operación
  const [compareFiles, setCompareFiles] = useState(null);
  const [compareSelectedPath, setCompareSelectedPath] = useState(null);
  const [diffMode, setDiffMode] = useState('side');

  useEffect(() => {
    let cancelled = false;
    const fetchCommits = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/github/history`, {
          params: { module_id: moduleId, limit: 30 },
        });
        if (!cancelled) setCommits(res.data?.commits || []);
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message;
        toast.error(isEs ? `Error al cargar historial: ${detail}` : `History failed: ${detail}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchCommits();
    return () => { cancelled = true; };
  }, [moduleId, isEs]);

  const handleCompare = async (sha) => {
    setBusy(sha + ':cmp');
    try {
      const res = await axios.post(`${API}/github/compare`, {
        module_id: moduleId,
        sha_left: sha,
        sha_right: null, // null = workspace local
      });
      const changedFiles = (res.data?.files || []).filter((f) => f.status !== 'unchanged');
      setCompareFiles(changedFiles);
      setCompareSelectedPath(changedFiles[0]?.path || null);
      if (changedFiles.length === 0) {
        toast.info(isEs ? 'Sin diferencias' : 'No differences');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(isEs ? `Compare falló: ${detail}` : `Compare failed: ${detail}`);
    } finally {
      setBusy(null);
    }
  };

  const handleRevert = async (sha, shortSha) => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      isEs
        ? `¿Revertir el módulo al commit ${shortSha}? Esta acción sobreescribe los archivos actuales.`
        : `Revert module to commit ${shortSha}? This overwrites current files.`
    );
    if (!ok) return;
    setBusy(sha + ':rv');
    try {
      const res = await axios.post(`${API}/github/revert`, {
        module_id: moduleId,
        sha,
      });
      if (res.data?.success) {
        toast.success(
          isEs
            ? `Revertido a ${shortSha} · ${res.data.file_count} archivos`
            : `Reverted to ${shortSha} · ${res.data.file_count} files`
        );
        window.dispatchEvent(new CustomEvent('damyxia-reload-internal-module', {
          detail: { moduleId },
        }));
        onClose?.();
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(isEs ? `Revert falló: ${detail}` : `Revert failed: ${detail}`);
    } finally {
      setBusy(null);
    }
  };

  const selectedFile = compareFiles?.find((f) => f.path === compareSelectedPath);

  return (
    <div className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="commit-history-panel">
      <div className="w-full max-w-5xl h-[85vh] bg-slate-950 border border-violet-500/40 rounded-xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-violet-950/30">
          <div className="flex items-center gap-2">
            <History className="w-5 h-5 text-violet-400" />
            <h3 className="text-sm font-semibold text-violet-300">
              {isEs ? 'Historial de commits' : 'Commit history'}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-white rounded"
            data-testid="commit-history-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 flex">
          {/* Lista de commits */}
          <div className="w-80 min-w-[260px] max-w-[360px] shrink-0 border-r border-slate-800 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-sm text-slate-400 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> {isEs ? 'Cargando…' : 'Loading…'}
              </div>
            ) : commits.length === 0 ? (
              <div className="p-4 text-sm text-slate-500">{isEs ? 'Sin commits' : 'No commits'}</div>
            ) : (
              <ul className="divide-y divide-slate-800">
                {commits.map((c) => (
                  <li key={c.sha} className="p-3 hover:bg-slate-900/50" data-testid={`commit-row-${c.short_sha}`}>
                    <div className="flex items-start gap-2">
                      <GitCommit className="w-4 h-4 mt-0.5 text-violet-400 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-slate-200 leading-snug line-clamp-2">{c.message}</div>
                        <div className="mt-1 text-[11px] text-slate-500 truncate">
                          <span className="font-mono text-violet-300">{c.short_sha}</span>
                          <span className="mx-1">·</span>
                          {c.author}
                          <span className="mx-1">·</span>
                          {(c.date || '').slice(0, 10)}
                        </div>
                        <div className="mt-2 flex items-center gap-1 flex-wrap">
                          <a
                            href={c.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded text-slate-400 hover:text-violet-300 hover:bg-violet-500/10"
                          >
                            <ExternalLink className="w-3 h-3" />
                            GitHub
                          </a>
                          <button
                            type="button"
                            onClick={() => handleCompare(c.sha)}
                            disabled={busy !== null}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded text-slate-400 hover:text-amber-300 hover:bg-amber-500/10 disabled:opacity-50"
                            data-testid={`commit-compare-${c.short_sha}`}
                          >
                            {busy === c.sha + ':cmp' ? <Loader2 className="w-3 h-3 animate-spin" /> : <GitCompare className="w-3 h-3" />}
                            {isEs ? 'Comparar' : 'Compare'}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRevert(c.sha, c.short_sha)}
                            disabled={busy !== null}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded text-slate-400 hover:text-rose-300 hover:bg-rose-500/10 disabled:opacity-50"
                            data-testid={`commit-revert-${c.short_sha}`}
                          >
                            {busy === c.sha + ':rv' ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                            {isEs ? 'Revertir' : 'Revert'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Diff panel a la derecha cuando hay compare activo */}
          <div className="flex-1 min-w-0 flex flex-col bg-slate-950">
            {compareFiles === null ? (
              <div className="p-6 text-sm text-slate-500">
                {isEs ? 'Haz clic en "Comparar" para ver diferencias contra tu Workspace actual.' : 'Click "Compare" to see differences against your current Workspace.'}
              </div>
            ) : compareFiles.length === 0 ? (
              <div className="p-6 text-sm text-emerald-300">
                {isEs ? 'Ese commit es idéntico al Workspace.' : 'That commit is identical to Workspace.'}
              </div>
            ) : (
              <div className="flex-1 min-h-0 flex">
                {/* lista de archivos cambiados */}
                <div className="w-48 min-w-[160px] shrink-0 border-r border-slate-800 overflow-y-auto py-1">
                  {compareFiles.map((f) => (
                    <button
                      key={f.path}
                      type="button"
                      onClick={() => setCompareSelectedPath(f.path)}
                      className={`w-full text-left px-2 py-1 text-xs truncate flex items-center gap-1.5 ${
                        compareSelectedPath === f.path ? 'bg-violet-500/20 text-violet-300' : 'text-slate-300 hover:bg-slate-700/40'
                      }`}
                      data-testid={`diff-file-${f.path}`}
                    >
                      <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${
                        f.status === 'added' ? 'bg-emerald-400' :
                        f.status === 'removed' ? 'bg-rose-400' :
                        f.status === 'modified' ? 'bg-amber-400' : 'bg-slate-500'
                      }`} />
                      <span className="truncate">{f.path}</span>
                    </button>
                  ))}
                </div>
                <div className="flex-1 min-h-0">
                  <DiffViewer file={selectedFile} mode={diffMode} onModeChange={setDiffMode} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommitHistoryPanel;
